/**
 * Auth API client for sponsor registration and login.
 *
 * Uses magic link authentication - email is pseudonymous identifier.
 * Fingerprint is captured for audit context, not identity.
 *
 * Security posture:
 * - Auth errors are non-enumerating (login never reveals if account exists)
 * - Session stored in httpOnly cookie
 * - Fingerprint signals captured for audit continuity
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
 *   The original repair introduced a persistent in-memory cache of
 *   the CSRF token. That violated the backend's single-use contract
 *   (CSRFStore.Validate deletes the token on success — see
 *   internal/auth/csrf.go:177-180). The second mutation in the same
 *   session would have re-sent the consumed token and surfaced as
 *   `csrf token invalid`.
 *
 *   This file now enforces:
 *     - ensureCSRFToken() issues a fresh `GET /api/auth/csrf` on
 *       EVERY call. There is no in-memory cache of the issued token.
 *     - The csrf_token cookie is informational only; the helper does
 *       not assume it is unconsumed.
 *     - Concurrent ensureCSRFToken() calls each issue their own GET,
 *       so two parallel unsafe mutations acquire two distinct tokens.
 *     - The "previous synchronous getCSRFToken() that returned the
 *       cache value" is preserved as `getCSRFTokenSync()` for tests
 *       and reads, but unsafe mutation adapters MUST await
 *       ensureCSRFToken() and use its return value directly.
 */

const API_BASE = '/api/auth';

// CSRF header name (from backend DoubleSubmitCookie pattern)
export const CSRF_HEADER_NAME = 'X-CSRF-Token';
const CSRF_COOKIE_NAME = 'csrf_token';

/**
 * Read CSRF token from `document.cookie` synchronously.
 *
 * Returns the raw value or `undefined`. This is a synchronous,
 * pure read path used by tests and sanity checks.
 *
 * IMPORTANT: under the single-use CSRF contract, a value read
 * here MAY have already been consumed (e.g. by another tab).
 * Unsafe mutation adapters MUST NOT use this value directly.
 * They MUST call `ensureCSRFToken()` and use the return value.
 *
 * Exported for shared use by other auth-aware API modules
 * (e.g. profile photo upload / delete). The CSRF contract is owned
 * by the backend and re-used here; do not duplicate the cookie name.
 */
export function getCSRFTokenSync(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(^| )${CSRF_COOKIE_NAME}=([^;]+)`));
  return match ? match[2] : undefined;
}

/**
 * CSRF bootstrap endpoint that issues a fresh token AND sets the
 * `csrf_token` cookie. Mounted at GET /api/auth/csrf.
 */
const CSRF_BOOTSTRAP_PATH = `${API_BASE}/csrf`;

/**
 * Synchronous alias for `getCSRFTokenSync()` kept for backward
 * compatibility with the original helper name. Under the
 * single-use CSRF contract, callers MUST NOT use this for unsafe
 * mutations without first calling `ensureCSRFToken()`. See
 * `getCSRFTokenSync` for the full contract.
 */
export const getCSRFToken = getCSRFTokenSync;

/**
 * Set the CSRF cookie to a specific value. Used by tests and by
 * the auth bootstrap hook when the backend returns a token in a
 * login/verify response. The helper does NOT validate the token;
 * the next /api/v1 mutation that carries the token will surface
 * a `session_required` variant if rejected.
 *
 * Under the single-use contract this is NOT a cache write; it is
 * a one-shot cookie seed for test fixtures. Production code
 * should never call this directly.
 */
export function setCSRFTokenForTests(token: string | undefined): void {
  if (typeof document === 'undefined') return;
  if (token === undefined) {
    document.cookie = `${CSRF_COOKIE_NAME}=; Path=/; Max-Age=0`;
    return;
  }
  document.cookie = `${CSRF_COOKIE_NAME}=${token}; Path=/`;
}

/**
 * Clear the CSRF cookie. Callers should call this on logout so
 * the next fresh login does not see a stale cookie value.
 *
 * Replaces the previous `clearCSRFTokenCache()` from CORRECTION00.
 * Under the single-use contract there is no in-memory cache to
 * clear; only the cookie is touched.
 */
export function clearCSRFTokenCache(): void {
  setCSRFTokenForTests(undefined);
}

/**
 * Ensure a fresh single-use CSRF token is available for the next
 * unsafe mutation.
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01 (CORRECTION01):
 *
 * Production contract: the backend's CSRFStore deletes a token
 * after a successful ValidateToken call (single-use). Therefore:
 *
 *   - The cookie MAY be present but already consumed; we MUST NOT
 *     trust it as a reusable authority.
 *   - A persistent in-memory cache would hand the same consumed
 *     token to the next mutation and surface as
 *     `csrf token invalid`.
 *   - The ONLY safe client strategy is: every unsafe mutation
 *     issues a fresh `GET /api/auth/csrf`, then immediately sends
 *     its state-changing request with the body token. The token
 *     is consumed by the backend on validation; the client never
 *     retains it across calls.
 *
 * Returns:
 *   - the freshly issued token string, OR
 *   - `undefined` if the bootstrap fetch failed (no session,
 *     network error, non-2xx).
 *
 * Callers that receive `undefined` MUST treat the request as
 * `session_required`. They MUST NOT fall back to the cookie
 * value.
 *
 * Concurrent unsafe mutations each call `ensureCSRFToken()` and
 * each get their own fresh token. There is intentionally no
 * in-flight promise sharing because that would merge two
 * single-use acquisitions into one (the second caller would
 * receive the token the first caller is about to consume).
 *
 * Production evidence that justifies the defensive GET:
 *   `document.cookie === ""` on
 *   `https://indeep.info/feed/compose` even though the session
 *   cookie is present. The CSRF cookie was not reaching the
 *   browser for some users.
 */
export async function ensureCSRFToken(): Promise<string | undefined> {
  if (typeof fetch === 'undefined') {
    return undefined;
  }

  try {
    const response = await fetch(CSRF_BOOTSTRAP_PATH, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      return undefined;
    }
    const body = (await response.json().catch(() => null)) as
      | { csrf_token?: unknown }
      | null;
    if (
      body &&
      typeof body === 'object' &&
      typeof body.csrf_token === 'string' &&
      body.csrf_token.length > 0
    ) {
      return body.csrf_token;
    }
    // Cookie may have been set by Set-Cookie even though the JSON
    // body didn't carry csrf_token. Re-read the cookie. We do not
    // retain this across calls; the next ensureCSRFToken() will
    // refetch.
    const retry = getCSRFTokenSync();
    if (retry) {
      return retry;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// Types matching backend DTOs
// REQ-AUTH-SIGNUP-EMAIL-ONLY-001: Registration no longer requires registration_kind
// REQ-AUTH-REGISTRATION-KIND-UNCLASSIFIED-001: Backend sets registration_kind to unclassified
export interface RegisterRequest {
  email: string;
}

export interface RegisterResponse {
  message: string;
  next_step?: 'verify_email';
  resend_available?: boolean;
}

// Special response when registration is attempted for an email that already
// has a pending verification - frontend should show check-email/resend flow
export interface RegisterPendingVerificationResponse {
  message: 'verification email already requested';
  next_step: 'verify_email';
  resend_available: true;
}

export interface LoginRequest {
  email: string;
  fingerprint?: FingerprintSignals;
}

export interface LoginResponse {
  message: string;
}

export interface VerifyRequest {
  token: string;
  fingerprint?: FingerprintSignals;
}

export interface VerifyResponse {
  session_token?: string;
  message: string;
}

export interface SessionResponse {
  session_id: string;
  account_id: string;
  registration_kind: 'friend' | 'sponsor' | '';
  has_sponsor_access: boolean;
  sponsor_tier: 'none' | 'sponsor';
  is_admin: boolean;
}

export interface LogoutResponse {
  message: string;
}

export interface ResendVerificationResponse {
  ok: boolean;
}

// OTP verification response
export interface OTPVerifyResponse {
  message: string;
}

// Fingerprint signals for audit context
// Raw signals are transient - only hash is persisted
export interface FingerprintSignals {
  user_agent: string;
  accept_language: string;
  screen: string;
  timezone: string;
  platform: string;
  hardware_concurrency: number;
  canvas_hash: string;
  webgl_vendor: string;
  webgl_renderer: string;
  audio_hash: string;
  fonts_hash: string;
  touch_support: boolean;
  color_depth: number;
  device_memory: number;
}

// Allowlisted safe error messages from backend
// These are documented public API error strings that can be shown to users.
// Backend validation errors are safe to display as they don't enumerate accounts.
const SAFE_ERROR_MESSAGES = new Set([
  'invalid email',
  'invalid request body',
  // REQ-AUTH-REGISTRATION-KIND-UNCLASSIFIED-001: Client-provided registration_kind rejected fail-closed
  'registration_kind not allowed',
  'could not send verification email. please try again later.',
  'could not send verification code. please try again later.',
  'registration unavailable',
  'already registered',
  // REQ-AUTH-LOGIN-OTP-SAME-ENTRY-001: OTP verification errors
  'invalid verification code',
]);

// API errors
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly safe: boolean = true,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Check if an error message is in the allowlist (can be safely displayed)
export function isSafeDisplayableError(message: string): boolean {
  return SAFE_ERROR_MESSAGES.has(message);
}

// Email validation - matches backend validation logic
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  const atIdx = trimmed.indexOf('@');
  if (atIdx <= 0 || atIdx >= trimmed.length - 1) {
    return false;
  }
  const dotIdx = trimmed.lastIndexOf('.');
  if (dotIdx <= atIdx || dotIdx >= trimmed.length - 1) {
    return false;
  }
  return true;
}

// Generic fetch wrapper for auth endpoints
async function authFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01:
  //
  // Most /api/auth/* endpoints run BEFORE the session exists
  // (login, register, OTP begin, OTP verify, resend). At that
  // point there is no session cookie and no CSRF token to
  // bootstrap, so calling ensureCSRFToken() would just issue a
  // 401 GET /api/auth/csrf and waste a round trip.
  //
  // Post-auth endpoints (logout, me) carry a session cookie and
  // can read the csrf_token cookie that verify/otp set as a side
  // effect. For those endpoints the synchronous getCSRFToken()
  // returns the canonical token. If the cookie is missing, we
  // fall through with no header and the backend's RequireCSRF
  // middleware will surface a 403 csrf_token_required.
  const csrfToken = getCSRFToken();

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
      ...options.headers,
    },
    credentials: 'include', // Include cookies for session
  });

  // Try to parse JSON response
  let data: { error?: string } | null = null;
  let message = 'request failed';
  let isSafe = false;
  
  try {
    data = await response.json();
    if (data && typeof data === 'object' && 'error' in data) {
      message = data.error;
      isSafe = isSafeDisplayableError(message);
    }
  } catch {
    // Non-JSON response - use generic message
    // This handles edge cases like HTML error pages
    isSafe = false;
  }

  if (!response.ok) {
    throw new AuthError(message, isSafe, response.status);
  }

  return data as T;
}

/**
 * Register a new account using magic link.
 * REQ-AUTH-SIGNUP-EMAIL-ONLY-001: Registration accepts email only.
 * REQ-AUTH-REGISTRATION-KIND-UNCLASSIFIED-001: Backend sets registration_kind to unclassified.
 * 
 * Returns success message regardless of whether email already exists.
 * 
 * @param email - User's email address
 */
export async function register(email: string): Promise<RegisterResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<RegisterResponse>('/register', {
    method: 'POST',
    body: JSON.stringify({ 
      email: email.trim(),
    }),
  });

  return response;
}

/**
 * Initiate magic link login.
 * Returns success message regardless of whether account exists.
 * This prevents email enumeration.
 * 
 * @param email - User's email address
 * @param fingerprint - Optional browser fingerprint signals for fraud-resistant audit context
 */
export async function login(email: string, fingerprint?: FingerprintSignals): Promise<LoginResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<LoginResponse>('/login', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Verify magic link token.
 * Fingerprint is optional but recommended for audit continuity.
 */
export async function verifyMagicLink(
  token: string,
  fingerprint?: FingerprintSignals
): Promise<VerifyResponse> {
  const response = await authFetch<VerifyResponse>('/verify', {
    method: 'POST',
    body: JSON.stringify({
      token,
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Complete magic link login with fingerprint.
 */
export async function completeLogin(
  token: string,
  fingerprint?: FingerprintSignals
): Promise<VerifyResponse> {
  const response = await authFetch<VerifyResponse>('/verify/complete', {
    method: 'POST',
    body: JSON.stringify({
      token,
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Initiate OTP-based login.
 * REQ-AUTH-LOGIN-OTP-SAME-ENTRY-001: Login uses same OTP endpoints as signup.
 * 
 * @param email - User's email address
 * @param fingerprint - Optional browser fingerprint signals for audit context
 */
export async function loginWithOTP(email: string, fingerprint?: FingerprintSignals): Promise<LoginResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<LoginResponse>('/login/otp', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * BeginOTP — ACT-INDEEP-AUTH-UNIFIED-ENTRY01.
 *
 * The unified email entry surface. Same public operation for signup and login.
 * The server resolves account state internally and produces exactly one
 * intended mail side-effect. The response is identical regardless of whether
 * the account is unknown, pending, active, or ineligible.
 *
 * Replaces the previous client-side choice between `registerWithOTP` and
 * `loginWithOTP`. Use this from any public entry surface (mobile login,
 * desktop login, resend). Do NOT call `registerWithOTP` or `loginWithOTP`
 * from canonical entry code — those remain only for legacy callers.
 *
 * Anti-enumeration: the wire response is `{"message": "if the email exists,
 * a verification code has been sent"}` for every account state. Never branch
 * the UI on the response.
 *
 * @param email - User's email address
 * @param fingerprint - Optional browser fingerprint signals for audit context
 */
export async function beginOTP(email: string, fingerprint?: FingerprintSignals): Promise<LoginResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<LoginResponse>('/otp', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Get current session info.
 * Returns null if not authenticated.
 */
export async function getSession(): Promise<SessionResponse | null> {
  try {
    const response = await fetch(`${API_BASE}/me`, {
      credentials: 'include',
    });

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new AuthError('session check failed', false);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof AuthError && !error.safe) {
      throw error;
    }
    return null;
  }
}

/**
 * Logout current session.
 */
export async function logout(): Promise<void> {
  try {
    await authFetch<LogoutResponse>('/logout', {
      method: 'POST',
    });
  } catch {
    // Ignore errors on logout
  }
}

/**
 * Resend verification email for pending account.
 * 
 * Security posture:
 * - Backend always returns generic success to prevent account enumeration
 * - Email validation is done client-side for UX, server validates again
 * - Any 2xx response means we show success message regardless of backend internals
 * - Fingerprint is optional - collection failure is non-blocking (fail-open)
 * 
 * @param email - The email address to resend verification for
 * @param fingerprint - Optional browser fingerprint signals for fraud-resistant audit context
 * @returns ResendVerificationResponse with ok:true on any 2xx
 */
export async function resendVerification(
  email: string,
  fingerprint?: FingerprintSignals
): Promise<ResendVerificationResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<ResendVerificationResponse>('/resend-verification', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Register a new account using OTP verification.
 * REQ-AUTH-SIGNUP-OTP-ONLY-001: Signup uses email OTP verification.
 * REQ-AUTH-REGISTRATION-KIND-UNCLASSIFIED-001: Registration_kind defaults to 'unclassified'.
 * 
 * @param email - User's email address
 * @param fingerprint - Optional browser fingerprint signals for fraud-resistant audit context
 */
export async function registerWithOTP(email: string, fingerprint?: FingerprintSignals): Promise<RegisterResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<RegisterResponse>('/register/otp', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}

/**
 * Verify OTP code and complete registration/login.
 * REQ-AUTH-OTP-SUCCESS-HOME-REDIRECT-001: After verification, user is authenticated.
 * 
 * @param email - User's email address
 * @param code - 4-digit OTP code
 * @param fingerprint - Optional browser fingerprint signals for audit context
 */
export async function verifyOTP(email: string, code: string, fingerprint?: FingerprintSignals): Promise<OTPVerifyResponse> {
  if (!isValidEmail(email)) {
    throw new AuthError('invalid email', true);
  }

  const response = await authFetch<OTPVerifyResponse>('/verify/otp', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      code: code.trim(),
      ...(fingerprint && { fingerprint }),
    }),
  });

  return response;
}
